import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, HttpError } from '../../lib/http';
import { Chip } from '../components/chip';
import { Modal } from '../components/modal';
import { useToast } from '../components/toast';
import {
    diagnosticCoverage,
    diagnosticEmptyText,
    diagnosticIssueId,
    diagnosticPath,
    diagnosticTraceUrl,
    issueStatusReason,
    issueTransitions
} from './infra/client-diagnostics';
import type {
    DiagnosticEvent,
    DiagnosticIssue,
    DiagnosticPage,
    IssueStatus
} from './infra/client-diagnostics';

export function ClientDiagnosticsPanel({ kind }: { kind: 'events' | 'issues' }) {
    const [search, setSearch] = useSearchParams();
    const linkedIssueId = kind === 'issues' ? diagnosticIssueId(search.get('issue')) : null;
    const [filters, setFilters] = useState<Record<string, string>>({ hours: '24' });
    const [cursor, setCursor] = useState('');
    const [history, setHistory] = useState<string[]>([]);
    const [event, setEvent] = useState<DiagnosticEvent | null>(null);
    const [issue, setIssue] = useState<DiagnosticIssue | null>(null);
    const linkedIssue = useQuery<{ issue: DiagnosticIssue }>({
        queryKey: ['admin', 'diagnostic-issue', linkedIssueId],
        queryFn: () => apiGet(`/api/admin/diagnostic-issues/${linkedIssueId}`),
        enabled: Boolean(linkedIssueId),
        retry: false
    });
    useEffect(() => {
        if (linkedIssueId && linkedIssue.data?.issue.id === linkedIssueId)
            setIssue(linkedIssue.data.issue);
    }, [linkedIssueId, linkedIssue.data]);
    const closeIssue = () => {
        setIssue(null);
        if (search.has('issue')) {
            const next = new URLSearchParams(search);
            next.delete('issue');
            setSearch(next, { replace: true });
        }
    };
    const path = diagnosticPath(kind, { ...filters, cursor });
    const query = useQuery<DiagnosticPage<DiagnosticEvent | DiagnosticIssue>>({
        queryKey: ['admin', 'diagnostics', path],
        queryFn: () => apiGet(path),
        retry: false
    });
    const coverage = diagnosticCoverage(query.data, query.error);
    const update = (key: string, value: string) => {
        setFilters((current) => ({ ...current, [key]: value }));
        setCursor('');
        setHistory([]);
    };
    const items = query.data?.items || [];
    return (
        <div className="stack-16">
            {linkedIssueId && linkedIssue.isError && (
                <div className="alert-bar" role="alert">
                    Linked issue evidence is unavailable or access is denied.
                </div>
            )}
            <header className="page-header">
                <div className="page-title-group">
                    <h1>{kind === 'events' ? 'Client diagnostics' : 'Grouped issues'}</h1>
                    <p>
                        {kind === 'events'
                            ? 'Captured frontend and native failures. The server enforces the 30-day window.'
                            : 'Staff work items from captured failures. Counts do not prove data loss.'}
                    </p>
                </div>
                <button
                    className="btn btn-ghost"
                    onClick={() => void query.refetch()}
                    disabled={query.isFetching}
                >
                    Refresh
                </button>
            </header>
            <section className="filter-bar" aria-label="Diagnostic filters">
                <select
                    className="select"
                    aria-label="Diagnostic time window"
                    value={filters.hours}
                    onChange={(e) => update('hours', e.target.value)}
                >
                    <option value="1">Last hour</option>
                    <option value="24">Last 24 hours</option>
                    <option value="168">Last 7 days</option>
                    <option value="720">Last 30 days</option>
                </select>
                <select
                    className="select"
                    aria-label="Diagnostic environment"
                    value={filters.environment || ''}
                    onChange={(e) => update('environment', e.target.value)}
                >
                    <option value="">All environments</option>
                    <option value="production">Production</option>
                    <option value="development">Development</option>
                </select>
                {kind === 'events' && (
                    <>
                        <select
                            className="select"
                            aria-label="Client origin"
                            value={filters.client_surface || ''}
                            onChange={(e) => update('client_surface', e.target.value)}
                        >
                            <option value="">All client origins</option>
                            <option value="electron_renderer">Desktop UI</option>
                            <option value="electron_main">Desktop native</option>
                            <option value="web_renderer">Web UI</option>
                        </select>
                        <input
                            className="form-input"
                            style={{ maxWidth: 220 }}
                            aria-label="Action code"
                            placeholder="Action code, e.g. import.intake"
                            value={filters.operation || ''}
                            onChange={(e) => update('operation', e.target.value)}
                        />
                        <input
                            className="form-input"
                            style={{ maxWidth: 220 }}
                            aria-label="Error code"
                            placeholder="Error code"
                            value={filters.error_code || ''}
                            onChange={(e) => update('error_code', e.target.value)}
                        />
                        <input
                            className="form-input"
                            style={{ maxWidth: 220 }}
                            aria-label="Stage"
                            placeholder="Stage"
                            value={filters.stage || ''}
                            onChange={(e) => update('stage', e.target.value)}
                        />
                        <input
                            className="form-input"
                            style={{ maxWidth: 220 }}
                            aria-label="Release"
                            placeholder="Release ID"
                            value={filters.release_sha || ''}
                            onChange={(e) => update('release_sha', e.target.value)}
                        />
                    </>
                )}
            </section>
            <section className="alert-bar" aria-live="polite">
                <div
                    className="alert"
                    data-severity={coverage.state === 'complete' ? 'info' : 'warning'}
                >
                    <span className="msg">
                        Coverage: {query.isPending ? 'loading' : coverage.state}.{' '}
                        {coverage.state !== 'complete' && 'Some failures may be missing.'}{' '}
                        {coverage.observed_at && `Observed ${coverage.observed_at}.`}
                    </span>
                </div>
            </section>
            {query.data?.window && (
                <p className="text-muted">
                    UTC window: {query.data.window.from} to {query.data.window.to}.
                </p>
            )}
            <section className="card">
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Severity</th>
                                <th>Action / stage</th>
                                <th>Code</th>
                                <th>Origin</th>
                                {kind === 'issues' && (
                                    <>
                                        <th>Status</th>
                                        <th>Observations</th>
                                        <th>Operations</th>
                                        <th>Accounts</th>
                                    </>
                                )}
                                <th>Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row) => {
                                if ('event_id' in row)
                                    return (
                                        <tr key={row.event_id}>
                                            <td>{row.occurred_at}</td>
                                            <td>
                                                <Chip
                                                    tone={
                                                        row.severity === 'critical' ||
                                                        row.severity === 'error'
                                                            ? 'danger'
                                                            : 'warning'
                                                    }
                                                >
                                                    {row.severity}
                                                </Chip>
                                            </td>
                                            <td>
                                                {row.operation}
                                                <div className="text-muted">{row.stage}</div>
                                            </td>
                                            <td className="mono">{row.error_code}</td>
                                            <td>{row.client_surface}</td>
                                            <td>
                                                <button
                                                    className="btn btn-ghost"
                                                    onClick={() => setEvent(row)}
                                                >
                                                    View event
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                return (
                                    <tr key={row.id}>
                                        <td>{row.last_seen}</td>
                                        <td>{row.severity}</td>
                                        <td>
                                            {row.action}
                                            <div className="text-muted">{row.stage}</div>
                                        </td>
                                        <td className="mono">{row.error_code}</td>
                                        <td>{row.client_origin}</td>
                                        <td>{row.issue_status}</td>
                                        <td>{row.observed_count}</td>
                                        <td>{row.known_operation_count}</td>
                                        <td>{row.affected_account_count}</td>
                                        <td>
                                            <button
                                                className="btn btn-ghost"
                                                onClick={() => setIssue(row)}
                                            >
                                                Review issue
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {!items.length && (
                    <div className="empty-state">
                        {query.isPending ? 'Loading captured rows…' : diagnosticEmptyText(coverage)}
                    </div>
                )}
            </section>
            <nav className="page-actions" aria-label="Diagnostic pagination">
                <button
                    className="btn btn-ghost"
                    disabled={!history.length || query.isFetching}
                    onClick={() => {
                        setCursor(history.at(-1) || '');
                        setHistory((current) => current.slice(0, -1));
                    }}
                >
                    Previous
                </button>
                <button
                    className="btn btn-ghost"
                    disabled={!query.data?.next_cursor || query.isFetching}
                    onClick={() => {
                        setHistory((current) => [...current, cursor]);
                        setCursor(query.data?.next_cursor || '');
                    }}
                >
                    Next
                </button>
            </nav>
            {event && <DiagnosticEventModal event={event} onClose={() => setEvent(null)} />}
            {issue && <DiagnosticIssueModal issue={issue} onClose={closeIssue} />}
        </div>
    );
}

function DiagnosticEventModal({ event, onClose }: { event: DiagnosticEvent; onClose: () => void }) {
    const trace = diagnosticTraceUrl(event);
    return (
        <Modal
            open
            onClose={onClose}
            title={event.error_code}
            subtitle={event.operation}
            width={720}
        >
            <dl className="kv-list">
                <dt>Event ID</dt>
                <dd className="mono">{event.event_id}</dd>
                <dt>Origin / domain</dt>
                <dd>
                    {event.client_surface} / {event.metadata.client?.failure_domain || 'unknown'}
                </dd>
                <dt>Stage</dt>
                <dd>{event.stage}</dd>
                <dt>Mutation state</dt>
                <dd>{event.metadata.client?.mutation_state || 'unknown'}</dd>
                <dt>Recovery required</dt>
                <dd>
                    {event.metadata.client?.recovery_required == null
                        ? 'Unknown'
                        : event.metadata.client.recovery_required
                          ? 'Yes'
                          : 'No'}
                </dd>
                <dt>Operation ID</dt>
                <dd className="mono">{event.metadata.client?.operation_id || 'Unknown'}</dd>
                <dt>Release</dt>
                <dd className="mono">{event.release_sha}</dd>
                <dt>Request ID</dt>
                <dd className="mono">{event.request_id || 'Unavailable'}</dd>
                <dt>Expiry</dt>
                <dd>{event.expires_at}</dd>
            </dl>
            {trace ? (
                <a className="btn btn-ghost" href={trace} target="_blank" rel="noreferrer">
                    Open request in Cloudflare
                </a>
            ) : (
                <p className="text-muted">Cloudflare trace unavailable.</p>
            )}
        </Modal>
    );
}

function DiagnosticIssueModal({ issue, onClose }: { issue: DiagnosticIssue; onClose: () => void }) {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const detail = useQuery<{
        issue: DiagnosticIssue;
        evidence: Array<{ event_id: string; occurred_at: string }>;
    }>({
        queryKey: ['admin', 'diagnostic-issue', issue.id],
        queryFn: () => apiGet(`/api/admin/diagnostic-issues/${issue.id}`),
        retry: false
    });
    const current = detail.data?.issue || issue;
    const [status, setStatus] = useState<IssueStatus | ''>('');
    const [ticket, setTicket] = useState('');
    const [relation, setRelation] = useState('customer_scope_confirmed');
    const [saving, setSaving] = useState(false);
    async function mutate(action: 'status' | 'tickets') {
        setSaving(true);
        try {
            await apiPost(
                `/api/admin/diagnostic-issues/${issue.id}/${action}`,
                action === 'status'
                    ? {
                          status,
                          expected_revision: current.revision,
                          reason: issueStatusReason(status as IssueStatus)
                      }
                    : { ticket_id: ticket, reason: relation }
            );
            await queryClient.invalidateQueries({ queryKey: ['admin', 'diagnostics'] });
            await detail.refetch();
            toast(action === 'status' ? 'Issue status updated' : 'Existing ticket linked', {
                kind: 'success'
            });
            setStatus('');
        } catch (error) {
            if (error instanceof HttpError && error.status === 409) {
                setStatus('');
                await detail.refetch();
            }
            toast(
                error instanceof HttpError && error.status === 409
                    ? 'Issue changed. Refresh and review the current revision.'
                    : 'Update failed',
                { kind: 'danger' }
            );
        } finally {
            setSaving(false);
        }
    }
    return (
        <Modal
            open
            onClose={onClose}
            title={current.error_code}
            subtitle={current.action}
            width={720}
        >
            <div className="stack-16">
                <p>
                    State: {current.issue_status}. Revision: {current.revision}. Mutation:{' '}
                    {current.mutation_state}. Recovery:{' '}
                    {current.recovery_required == null
                        ? 'unknown'
                        : current.recovery_required
                          ? 'required'
                          : 'not required'}
                    .
                </p>
                <p>
                    {current.observed_count} observations · {current.known_operation_count} distinct
                    operations · {current.affected_account_count} affected accounts.
                </p>
                {detail.isError && (
                    <p role="alert">
                        Current evidence is unavailable. Refresh before changing this issue.
                    </p>
                )}
                <label className="form-field">
                    Next status
                    <select
                        className="select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as IssueStatus | '')}
                    >
                        <option value="">Choose a status</option>
                        {issueTransitions(current.issue_status).map((value) => (
                            <option key={value} value={value}>
                                {value}
                            </option>
                        ))}
                    </select>
                </label>
                <button
                    className="btn btn-primary"
                    disabled={!status || saving || !detail.data || detail.isError}
                    onClick={() => void mutate('status')}
                >
                    Save reviewed status
                </button>
                <label className="form-field">
                    Existing support ticket UUID
                    <input
                        className="form-input"
                        style={{ maxWidth: 220 }}
                        value={ticket}
                        onChange={(e) => setTicket(e.target.value)}
                    />
                </label>
                <label className="form-field">
                    Relation
                    <select
                        className="select"
                        value={relation}
                        onChange={(e) => setRelation(e.target.value)}
                    >
                        <option value="customer_scope_confirmed">Customer scope confirmed</option>
                        <option value="staff_assessed_relation">Staff assessed relation</option>
                    </select>
                </label>
                <button
                    className="btn btn-ghost"
                    disabled={
                        !/^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(
                            ticket
                        ) ||
                        saving ||
                        !detail.data ||
                        detail.isError
                    }
                    onClick={() => void mutate('tickets')}
                >
                    Link existing ticket
                </button>
                <p className="text-muted">
                    This records a relation. It does not create a ticket or send a customer message.
                </p>
                <h3>Recent evidence</h3>
                {(detail.data?.evidence || []).map((row) => (
                    <p className="mono" key={row.event_id}>
                        {row.occurred_at} · {row.event_id}
                    </p>
                ))}
            </div>
        </Modal>
    );
}
